// The heat-matrix overlay that appears behind a single selected brand: a grid of bubbles
// counting how many respondents scored that brand at each (x, y) attribute pair.
//
// This is a pure function of props on purpose. It used to be injected imperatively with
// chart.addSeries() after the chart had drawn, which fought the React wrapper: that wrapper
// calls chart.update(options, true, oneToOne) on every options change, and with oneToOne
// Highcharts removes any series absent from the passed options -- so the overlay was added
// and then destroyed on the next render. Building it here lets it travel with the options.

import { hexToRgba } from '@aytm/app/stats/lib/render/colors'
import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'

// Id of the overlay series. Not a brand: helpers that iterate brand series skip this id.
export const OVERLAY_SERIES_ID = '-1'

const TRANSPARENT = hexToRgba('#cccccc', 0)

// Mirrors bubbleConfig: a null radius value falls back to the top of the score range, which
// is what it uses for plotOptions.bubble.zMax.
const selectedZ = ({ modelData, position, sortedScores }) => {
  const z = modelData?.data?.[`qv${position}`]?.data?.[0]?.[2]
  return z == null ? sortedScores[sortedScores.length - 1] : z
}

const buildMatrix = ({ answer, qv, i1, i2 }) => {
  const l = answer.raw[0].scores.length
  const matrix = Array.from({ length: l }, () => Array.from({ length: l }, () => 0))
  let matrixMax = 0

  for (let idx = 0; idx < answer.raw.length; idx++) {
    const resp = answer.raw[idx]
    const attr1 = resp.data[qv][i1]
    const attr2 = resp.data[qv][i2]
    if (attr1 === -1 || attr2 === -1) continue

    matrix[attr1 - 1][attr2 - 1] += answer.weights ? answer.weights[idx] : 1
    matrixMax = Math.max(matrixMax, matrix[attr1 - 1][attr2 - 1])
  }

  return { matrix, matrixMax }
}

// Data labels start invisible and are revealed on hover; the chart has no tooltip.
const pointEvents = {
  mouseOver: function () {
    if (!this.dataLabel?.element) return
    const dl = this.dataLabel.element
    dl.setAttribute('visibility', 'visible')
    dl.setAttribute('opacity', '1')
    const text = dl.querySelector('text')
    if (text) {
      text.style.color = '#000000'
      text.style.fill = '#000000'
    }
  },
  mouseOut: function () {
    if (!this.dataLabel?.element) return
    const dl = this.dataLabel.element
    dl.setAttribute('visibility', 'hidden')
    dl.setAttribute('opacity', '0')
    const text = dl.querySelector('text')
    if (text) {
      text.style.color = TRANSPARENT
      text.style.fill = TRANSPARENT
    }
  }
}

// `selectedId` is a brand series id ("1".."N") when exactly one brand is showing, else null.
export const buildOverlaySeries = ({
  answer,
  modelData,
  sortedScores,
  selectedId,
  quadrantXAxisValue,
  quadrantYAxisValue
}) => {
  if (selectedId == null) return null
  if (!answer?.raw?.length || !answer.raw[0]?.scores?.length) return null
  if (!sortedScores?.length) return null

  const position = Number(selectedId)
  // Series ids are 1-based positions; answer.raw rows are indexed from 0.
  const qv = position - 1
  if (!Number.isInteger(qv) || qv < 0) return null

  const { matrix, matrixMax } = buildMatrix({
    answer,
    qv,
    i1: Number(quadrantXAxisValue),
    i2: Number(quadrantYAxisValue)
  })
  if (matrixMax <= 0) return null

  // Keep the busiest cell just under the selected brand's own bubble.
  const max = selectedZ({ modelData, position, sortedScores }) * 0.9
  const scale = max >= matrixMax ? 1 : max / matrixMax

  const scoreMin = sortedScores[0]
  const scoreMax = sortedScores[sortedScores.length - 1]
  const scoreStep = (scoreMax - scoreMin) / (sortedScores.length - 1)

  const data = []
  for (let i = 0; i < matrix.length; i++) {
    for (let j = 0; j < matrix[i].length; j++) {
      if (matrix[i][j] <= 0) continue
      data.push({
        x: (i + 1) * scoreStep,
        y: (j + 1) * scoreStep,
        z: matrix[i][j] * scale,
        name: formatAmounts(matrix[i][j])
      })
    }
  }

  if (!data.length) return null

  return {
    id: OVERLAY_SERIES_ID,
    allowPointSelect: false,
    color: '#cccccc',
    cursor: 'default',
    data,
    dataLabels: {
      align: 'center',
      color: TRANSPARENT,
      enabled: true,
      formatter: function () {
        return this.point.name
      },
      shadow: false,
      style: {
        fontSize: '14px',
        fontWeight: 'normal',
        lineHeight: '14px',
        textShadow: 'none'
      },
      useHTML: false,
      verticalAlign: 'middle',
      zIndex: 2
    },
    marker: {
      fillColor: '#cccccc',
      fillOpacity: 0.6,
      lineWidth: 0,
      radius: 0
    },
    point: { events: pointEvents },
    states: { hover: { enabled: false } },
    zIndex: 1
  }
}
